import { useState, useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAuth } from "firebase/auth";

import CircularProgress from "../../components/generic/CircularProgress";
import { getUserDB } from "../auth/AuthServices";
import { getReciversUser } from "../backend/UserService";
import { setUser, setUsers } from "../../redux/slices/userSlice";
import { sortAsc } from "../../functions";

// The data a user must have before being let through.
//
// Internal staff fill in a natural-person record with their place in the
// institution (entity, dependency, level, position). Those fields do not apply
// to a company user: their institutional data is the COMPANY's and lives in the
// `entities` collection, not in their personal record. With the usual list they
// would be stuck on /profile filling in boxes that are not theirs.
//
// Mind the name: `entity` is the employee's free-text field. The link to a
// company is called `entityNit`.
const STAFF_REQUIRED_FIELDS = [
  "usertitle", "name", "lastName", "city", "phone", "address",
  "email", "nuip", "entity", "dependence", "level", "position",
];

const ENTITY_REQUIRED_FIELDS = ["name", "lastName", "nuip", "phone"];

const isEntityUser = (user) => !!user && user.userType === "ENTITY";

const isProfileComplete = (user) => {
  const required = isEntityUser(user) ? ENTITY_REQUIRED_FIELDS : STAFF_REQUIRED_FIELDS;
  return required.every((field) => !!user[field]);
};

export default function ProtectedRoute() {
  const dispatch = useDispatch();
  const userAuth = useSelector((state) => state.user.value);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (!userAuth) {
      const unsubscribe = getAuth().onAuthStateChanged(async (loginUser) => {
        if (loginUser) {
          const response = await getUserDB(loginUser);
          // getUserDB (and the services under it) return null on error — which
          // is exactly what happens as the token is torn down during sign-out,
          // when onAuthStateChanged fires one more time. Dereferencing
          // response.user then threw "Cannot read properties of null". Bail out
          // quietly: with no profile there is nothing to put in the store.
          if (response && response.user) {
            dispatch(setUser(response.user));
            // The full staff directory is NOT loaded for a company user: they
            // have nobody to write to inside the institution, and there is no
            // reason for a third party to walk away with the employee list.
            if (!isEntityUser(response.user)) {
              const roster = await getReciversUser(response.user.token);
              if (roster && roster.users) {
                dispatch(setUsers(roster.users.sort(sortAsc('name'))));
              }
            }
          }
        }
        setCheckingAuth(false);
      });
      return () => unsubscribe();
    } else {
      setCheckingAuth(false);
    }
  }, [dispatch, userAuth]);

  if (checkingAuth) {
    return <CircularProgress />;
  }

  if (userAuth) {
    if (location.pathname !== "/profile" && !isProfileComplete(userAuth)) {
      return <Navigate to="/profile" />
    }

    // If the user is on the login route or any undefined one, send them home.
    // This used to point at /dashboard, which is not a route: it only worked by
    // ricochet because the "*" catch-all redirects to /home.
    const shouldRedirect =
      location.pathname === "/login" || location.pathname === "*";
    return shouldRedirect ? <Navigate to="/home" replace /> : <Outlet />;
  }

  return <Navigate to="/login" replace />;
}
